import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { Settings, LogOut, Link as LinkIcon, Users, CheckCircle2, AlertCircle, AlertTriangle, User, Edit2, Key, Unlink } from 'lucide-react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type LearnerAccount, type ParentAccount, type AccountsState, getLinkedParents } from '../../../accounts';
import { AppState } from '../../../app/types';
import { useLinkChild } from '../hooks/useLinkChild';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  viewingLearnerId: string | undefined;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
}
export function SettingsPage({
  accounts,
  activeAccount,
  linkedLearners,
  linkParentToLearnerById
}: ParentPageProps) {
  const {
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updatePassword,
    unlinkParentFromLearner
  } = useAppStore();
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    warnings: linkWarnings,
    hasGatingWarning: linkHasGatingWarning,
    hasBlockingWarning: linkHasBlockingWarning,
    handleSubmit: handleLinkChild,
    handleConfirmLink,
    handleCancelLink
  } = useLinkChild({
    accounts,
    parentId: activeAccount.id,
    linkedLearners,
    linkParentToLearnerById
  });
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount.displayName);
  const [editEmail, setEditEmail] = useState(activeAccount.email || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'parent',
    updatePassword
  });
  // Unlink state
  const [unlinkLearnerId, setUnlinkLearnerId] = useState<string | null>(null);
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    setIsEditingProfile(false);
  };
  const handleConfirmUnlink = () => {
    if (!unlinkLearnerId) return;
    const learnerId = unlinkLearnerId;
    const learner = linkedLearners.find(l => l.id === learnerId);
    const learnerName = learner?.displayName ?? 'Kind';
    unlinkParentFromLearner(activeAccount.id, learnerId);
    setUnlinkLearnerId(null);
    // D-P3: undo-toast mirroring the teacher pattern. Re-linking a parent is
    // idempotent — `linkParentToLearnerById` recreates the parent↔learner
    // relationship without disturbing the learner account itself.
    toast.success(`${learnerName} is ontkoppeld`, {
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          linkParentToLearnerById(activeAccount.id, learnerId);
          toast.success(`${learnerName} is weer gekoppeld.`);
        }
      },
      duration: 5000
    });
  };
  return <PageLayout title="Instellingen" description="Beheer je ouderaccount en gekoppelde kinderen" data-id="element-7320">
      <div className="max-w-3xl space-y-6" data-id="element-7321">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Jouw persoonlijke gegevens" icon={Settings} accentColor="bg-emerald-100 text-emerald-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount.displayName);
        setEditEmail(activeAccount.email || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-7323">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7324" /> Bewerk
              </Button> : null} data-id="element-7322">
          <div className="space-y-6" data-id="element-7325">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7326">
                <div data-id="element-7327">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7328">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-7329" />
                </div>
                <div data-id="element-7330">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7331">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-7332" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-7333">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-7334">
                    Annuleren
                  </Button>
                  <Button onClick={handleSaveProfile} variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-7335">
                    Opslaan
                  </Button>
                </div>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7336">
                <div data-id="element-7337">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7338">
                    Naam
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7339">
                    {activeAccount.displayName}
                  </div>
                </div>
                <div data-id="element-7340">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7341">
                    E-mailadres
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7342">
                    {activeAccount.email || <span className="text-slate-400 italic" data-id="element-7343">
                        Niet ingesteld
                      </span>}
                  </div>
                </div>
              </div>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-emerald-100 text-emerald-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-7345">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7346" /> Wijzig
              </Button> : null} data-id="element-7344">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-7347">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7348">
                <div className="sm:col-span-2" data-id="element-7349">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7350">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-7351" />
                </div>
                <div data-id="element-7352">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7353">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-7354" />
                </div>
                <div data-id="element-7355">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7356">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-7357" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-7358">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7359" />
                  <p data-id="element-7360">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-7361">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7362" />
                  <p data-id="element-7363">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-7364">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-7365">
                  Annuleren
                </Button>
                <Button type="submit" variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-7366">
                  Wachtwoord opslaan
                </Button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-7367">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Linked Children */}
        <SettingsSection title="Gekoppelde Kinderen" description="Beheer de kinderen die aan jouw account zijn gekoppeld" icon={Users} accentColor="bg-teal-100 text-teal-600" data-id="element-7368">
          {linkedLearners.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-7369">
              {linkedLearners.map(learner => {
            const otherParents = getLinkedParents(accounts, learner.id).filter(p => p.id !== activeAccount.id);
            return <div key={learner.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-teal-200 transition-colors" data-id="element-7370">
                    <div className="flex items-center justify-between" data-id="element-7371">
                      <div className="flex items-center gap-3" data-id="element-7372">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg" data-id="element-7373">
                          {learner.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div data-id="element-7374">
                          <div className="font-bold text-slate-900" data-id="element-7375">
                            {learner.displayName}
                          </div>
                          <div className="text-xs font-medium text-slate-500" data-id="element-7376">
                            {learner.username}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-7377">
                        <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-7378">
                          Gekoppeld
                        </div>
                        <button onClick={() => setUnlinkLearnerId(learner.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Ontkoppelen" data-id="element-7379">
                          <Unlink className="w-4 h-4" data-id="element-7380" />
                        </button>
                      </div>
                    </div>
                    {otherParents.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-7381">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-7382">
                          Ook gekoppeld
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-7383">
                          {otherParents.map(p => <span key={p.id} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-7384">
                              <User className="w-3 h-3" data-id="element-7385" />
                              {p.displayName}
                            </span>)}
                        </div>
                      </div>}
                  </div>;
          })}
            </div> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed mb-8" data-id="element-7386">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3" data-id="element-7387">
                <Users className="w-6 h-6" data-id="element-7388" />
              </div>
              <p className="text-slate-500 font-medium" data-id="element-7389">
                Er zijn nog geen kinderen gekoppeld aan dit account.
              </p>
            </div>}

          <div className="pt-6 border-t border-slate-100" data-id="element-7390">
            <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-7391">
              <LinkIcon className="w-4 h-4 text-teal-500" data-id="element-7392" />
              Nieuw kind koppelen
            </h4>
            <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-7393">
              Voer de 6-cijferige koppelcode in die je van de docent hebt
              gekregen of die het kind in zijn/haar profiel kan vinden.
            </p>

            <form onSubmit={handleLinkChild} className="flex flex-col sm:flex-row gap-3" data-id="element-7394">
              <Input variant="admin" value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder="Bijv. ABCDEF" className="uppercase font-mono tracking-wider" maxLength={6} data-id="element-7395" />
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto" data-id="element-7396">
                Koppelen
              </Button>
            </form>

            {error && <div className="mt-4 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3.5 rounded-lg border border-rose-100" data-id="element-7397">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" data-id="element-7398" />
                <p className="font-medium" data-id="element-7399">{error}</p>
              </div>}

            {success && <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3.5 rounded-lg border border-emerald-100" data-id="element-7400">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-500" data-id="element-7401" />
                <p className="font-medium" data-id="element-7402">{success}</p>
              </div>}

            {/* Child preview before confirming link */}
            {pendingLearner && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`mt-4 p-4 border rounded-xl ${linkHasGatingWarning ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'}`} data-id="element-7403">
                <div className="flex items-center gap-3 mb-3" data-id="element-7404">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${linkHasGatingWarning ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-600'}`} data-id="element-7405">
                    <User className="w-5 h-5" data-id="element-7406" />
                  </div>
                  <div data-id="element-7407">
                    <p className="font-bold text-slate-900" data-id="element-7408">
                      {pendingLearner.displayName}
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-7409">
                      {pendingLearner.grade ? `Groep ${pendingLearner.grade}` : 'Groep nog niet ingesteld'}
                    </p>
                  </div>
                </div>

                {/* Structured linkage warnings from the unified validator. */}
                {linkWarnings.length > 0 && <ul className="mb-3 space-y-2" data-id="element-7410">
                    {linkWarnings.map((w, idx) => {
                const isBlocking = w.severity === 'blocking';
                const Icon = isBlocking ? AlertCircle : AlertTriangle;
                const palette = isBlocking ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-300 text-amber-900';
                return <li key={`${w.type}-${idx}`} className={`flex items-start gap-2 p-3 border rounded-lg text-sm ${palette}`} data-id="element-7411">
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7412" />
                          <span className="font-medium" data-id="element-7413">{w.message}</span>
                        </li>;
              })}
                  </ul>}

                <div className="flex gap-2 justify-end" data-id="element-7414">
                  <Button variant="secondary" onClick={handleCancelLink} className="text-sm" data-id="element-7415">
                    Annuleren
                  </Button>
                  <Button onClick={handleConfirmLink} disabled={linkHasBlockingWarning} className={`text-sm font-bold ${linkHasBlockingWarning ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : linkHasGatingWarning ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}`} data-id="element-7416">
                    <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-7417" />
                    {linkHasGatingWarning ? 'Bevestig toch koppeling' : 'Bevestig koppeling'}
                  </Button>
                </div>
              </motion.div>}
          </div>
        </SettingsSection>

        {/* Logout */}
        <SettingsSection title="Uitloggen" description="Log uit van je ouderaccount" icon={LogOut} accentColor="bg-rose-100 text-rose-600" className="bg-rose-50/30 border-rose-100" data-id="element-7418">
          <div className="flex justify-end" data-id="element-7419">
            <Button variant="secondary" className="w-full sm:w-auto text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300" onClick={nav.handleLogout} data-id="element-7420">
              <LogOut className="w-4 h-4 mr-2" data-id="element-7421" />
              Uitloggen
            </Button>
          </div>
        </SettingsSection>
      </div>

      <ConfirmDialog open={!!unlinkLearnerId} onCancel={() => setUnlinkLearnerId(null)} onConfirm={handleConfirmUnlink} title="Kind ontkoppelen" message={`Weet je zeker dat je jezelf wilt ontkoppelen van ${linkedLearners.find(l => l.id === unlinkLearnerId)?.displayName}? Je kunt daarna geen voortgang meer inzien.`} confirmLabel="Ja, ontkoppelen" cancelLabel="Annuleren" variant="danger" data-id="element-7422" />
    </PageLayout>;
}